import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from './Login';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

// ─── STYLES ───────────────────────────────────────────────────────────────────
const Page = styled.div`
  min-height: 100vh;
  background: #f8fafc;
  padding-top: 48px;
  padding-bottom: 6rem;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #334155 0%, #475569 100%);
  padding: 1.2rem 1.25rem 1.5rem;
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  h1 { margin: 0; font-size: 1.15rem; font-weight: 700; }
  p  { margin: 0.2rem 0 0; font-size: 0.78rem; opacity: 0.85; }
`;

const AddBtn = styled(motion.button)`
  background: rgba(255,255,255,0.2);
  border: 1.5px solid rgba(255,255,255,0.5);
  color: white;
  border-radius: 0.6rem;
  padding: 0.5rem 1rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
`;

const Content = styled.div`
  padding: 1rem 1.25rem;
`;

const CategoryGroup = styled.div`
  margin-bottom: 1.2rem;
`;

const CategoryTitle = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #9ca3af;
  margin-bottom: 0.5rem;
  padding-left: 0.25rem;
`;

const Card = styled(motion.div)`
  background: white;
  border-radius: 1rem;
  padding: 1rem;
  margin-bottom: 0.6rem;
  box-shadow: 0 1px 6px rgba(0,0,0,0.06);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  opacity: ${p => p.inactive ? 0.5 : 1};
`;

const ServiceIcon = styled.div`
  width: 42px; height: 42px;
  background: #f1f5f9;
  border-radius: 0.6rem;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.2rem;
  flex-shrink: 0;
`;

const ServiceInfo = styled.div`
  flex: 1;
  min-width: 0;
  .name { font-size: 0.9rem; font-weight: 600; color: #1f1f1f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .desc { font-size: 0.75rem; color: #6b7280; margin-top: 0.1rem; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .meta { display: flex; gap: 0.5rem; margin-top: 0.35rem; }
`;

const Tag = styled.span`
  font-size: 0.68rem;
  font-weight: 600;
  background: ${p => p.bg || '#f3f4f6'};
  color: ${p => p.color || '#6b7280'};
  padding: 0.18rem 0.5rem;
  border-radius: 20px;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex-shrink: 0;
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 0.2rem;
  border-radius: 0.3rem;
  &:hover { background: #f3f4f6; }
`;

const Empty = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #9ca3af;
  .icon { font-size: 2.5rem; display: block; margin-bottom: 0.75rem; }
  p { font-size: 0.85rem; margin: 0; }
`;

// ─── OVERLAY / FORM ───────────────────────────────────────────────────────────
const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 300;
  display: flex; align-items: flex-end; justify-content: center;
`;

const Sheet = styled(motion.div)`
  background: #fff;
  border-radius: 1.2rem 1.2rem 0 0;
  padding: 1.5rem;
  width: 100%; max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;

  h3 { margin: 0 0 1rem; font-size: 1rem; color: #1f1f1f; }
  label { font-size: 0.78rem; color: #555; display: block; margin-bottom: 0.2rem; }
  input, textarea, select {
    width: 100%; padding: 0.6rem 0.8rem;
    border: 1.5px solid #e5e7eb; border-radius: 0.5rem;
    font-size: 0.9rem; margin-bottom: 0.8rem; box-sizing: border-box;
    &:focus { border-color: #334155; outline: none; }
  }
  textarea { resize: vertical; min-height: 60px; }
`;

const Row = styled.div`display: flex; gap: 0.6rem;`;

const SaveBtn = styled.button`
  background: #334155; color: white; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.95rem; font-weight: 600; cursor: pointer;
  flex: 2;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const CancelBtn = styled.button`
  background: #f3f4f6; color: #555; border: none; border-radius: 0.6rem;
  padding: 0.75rem; font-size: 0.9rem; cursor: pointer; flex: 1;
`;

const CATEGORIES = ['Cabelo', 'Unhas', 'Sobrancelha & Cílios', 'Estética', 'Maquiagem', 'Massagem', 'Geral'];
const ICONS = { 'Cabelo': '💇', 'Unhas': '💅', 'Sobrancelha & Cílios': '👁️', 'Estética': '✨', 'Maquiagem': '💄', 'Massagem': '💆', 'Geral': '✂️' };

function ServiceForm({ initial, onClose, onSaved }) {
  const { token } = useAuth();
  const isEdit = !!initial?._id;

  const [form, setForm] = useState({
    name: initial?.name || '',
    description: initial?.description || '',
    category: initial?.category || 'Geral',
    price: initial?.price ?? '',
    duration: initial?.duration ?? 60,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || form.price === '') return;
    setSaving(true);
    try {
      const url = isEdit ? `${API}/services/${initial._id}` : `${API}/services`;
      const r = await fetch(url, {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ...form, price: parseFloat(form.price), duration: parseInt(form.duration) })
      });
      if (r.ok) onSaved(await r.json(), isEdit);
    } catch {}
    setSaving(false);
  };

  return (
    <Overlay onClick={onClose}>
      <Sheet
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        onClick={e => e.stopPropagation()}
      >
        <h3>{isEdit ? 'Editar serviço' : 'Novo serviço'}</h3>

        <label>Nome do serviço *</label>
        <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ex: Escova progressiva" />

        <label>Categoria</label>
        <select value={form.category} onChange={e => set('category', e.target.value)}>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>

        <label>Descrição</label>
        <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Detalhes sobre o serviço..." />

        <Row>
          <div style={{ flex: 1 }}>
            <label>Preço (R$) *</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={e => set('price', e.target.value)} placeholder="0,00" />
          </div>
          <div style={{ flex: 1 }}>
            <label>Duração (min)</label>
            <input type="number" min="5" step="5" value={form.duration} onChange={e => set('duration', e.target.value)} />
          </div>
        </Row>

        <Row>
          <CancelBtn onClick={onClose}>Cancelar</CancelBtn>
          <SaveBtn onClick={handleSave} disabled={saving || !form.name || form.price === ''}>
            {saving ? 'Salvando...' : isEdit ? 'Salvar alterações' : 'Criar serviço'}
          </SaveBtn>
        </Row>
      </Sheet>
    </Overlay>
  );
}

// ─── COMPONENTE PRINCIPAL ─────────────────────────────────────────────────────
const ServicoEditor = () => {
  const { token } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | {} (novo) | { ...service } (editar)

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) setServices(await r.json());
      } catch {}
      setLoading(false);
    })();
  }, [token]);

  const handleSaved = (saved, isEdit) => {
    setServices(prev =>
      isEdit ? prev.map(s => s._id === saved._id ? saved : s) : [...prev, saved]
    );
    setForm(null);
  };

  const toggleActive = async (service) => {
    try {
      const r = await fetch(`${API}/services/${service._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ active: !service.active })
      });
      if (r.ok) {
        const updated = await r.json();
        setServices(prev => prev.map(s => s._id === updated._id ? updated : s));
      }
    } catch {}
  };

  const handleDelete = async (serviceId) => {
    if (!window.confirm('Remover este serviço permanentemente?')) return;
    try {
      const r = await fetch(`${API}/services/${serviceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (r.ok) setServices(prev => prev.filter(s => s._id !== serviceId));
    } catch {}
  };

  // Agrupar por categoria
  const grouped = services.reduce((acc, s) => {
    const cat = s.category || 'Geral';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(s);
    return acc;
  }, {});

  const totalAtivos = services.filter(s => s.active).length;

  return (
    <Page>
      <Header>
        <div>
          <h1>Meus Serviços</h1>
          <p>{totalAtivos} serviço{totalAtivos !== 1 ? 's' : ''} ativo{totalAtivos !== 1 ? 's' : ''}</p>
        </div>
        <AddBtn whileTap={{ scale: 0.95 }} onClick={() => setForm({})}>
          + Novo
        </AddBtn>
      </Header>

      <Content>
        {loading ? (
          <Empty>
            <span className="icon">⏳</span>
            <p>Carregando serviços...</p>
          </Empty>
        ) : services.length === 0 ? (
          <Empty>
            <span className="icon">💅</span>
            <p>Ainda não há serviços cadastrados.<br />Toque em <strong>+ Novo</strong> para começar.</p>
          </Empty>
        ) : (
          Object.entries(grouped).map(([cat, items]) => (
            <CategoryGroup key={cat}>
              <CategoryTitle>{ICONS[cat] || '✂️'} {cat}</CategoryTitle>
              <AnimatePresence>
                {items.map((s, i) => (
                  <Card
                    key={s._id}
                    inactive={!s.active ? 1 : 0}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: s.active ? 1 : 0.5, y: 0 }}
                    transition={{ delay: i * 0.04 }}
                  >
                    <ServiceIcon>{ICONS[s.category] || '✂️'}</ServiceIcon>
                    <ServiceInfo>
                      <div className="name">{s.name}</div>
                      {s.description && <div className="desc">{s.description}</div>}
                      <div className="meta">
                        <Tag bg="#f1f5f9" color="#334155">R$ {s.price.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}</Tag>
                        <Tag bg="#f3f4f6" color="#6b7280">⏱ {s.duration} min</Tag>
                        {!s.active && <Tag bg="#fee2e2" color="#dc2626">Inativo</Tag>}
                      </div>
                    </ServiceInfo>
                    <Actions>
                      <IconBtn title="Editar" onClick={() => setForm(s)}>✏️</IconBtn>
                      <IconBtn title={s.active ? 'Desativar' : 'Ativar'} onClick={() => toggleActive(s)}>
                        {s.active ? '👁️' : '🚫'}
                      </IconBtn>
                      <IconBtn title="Remover" onClick={() => handleDelete(s._id)}>🗑️</IconBtn>
                    </Actions>
                  </Card>
                ))}
              </AnimatePresence>
            </CategoryGroup>
          ))
        )}
      </Content>

      {form !== null && (
        <ServiceForm
          initial={form._id ? form : null}
          onClose={() => setForm(null)}
          onSaved={handleSaved}
        />
      )}
    </Page>
  );
};

export default ServicoEditor;
